/**
 * useRole Hook
 *
 * React Query hook for fetching a single role by UUID.
 * Used for role detail views and edit forms.
 *
 * @module domains/personnel/presentation/hooks/useRole
 */

import { useQuery, UseQueryResult } from 'react-query';
import { getRoleUseCases } from '@personnel/composition';
import type { Role } from '@personnel/domain/entities/Role';
import { ROLES_QUERY_KEYS } from './useRoleList';

/**
 * Options for useRole hook
 */
export interface UseRoleOptions {
  /** Whether to enable the query (default: true) */
  enabled?: boolean;
}

/**
 * Return type for useRole hook
 */
export interface UseRoleResult {
  /** Role data (null if not loaded) */
  role: Role | null;
  /** Whether the query is loading */
  isLoading: boolean;
  /** Whether the query errored */
  isError: boolean;
  /** Error object (null if no error) */
  error: Error | null;
  /** Function to manually refetch */
  refetch: () => void;
}

/**
 * Hook for fetching a single role by UUID
 *
 * Uses React Query for caching, refetching, and state management.
 * Use Case is obtained from Composition Root (@personnel/composition).
 *
 * @param {string} uuid - Role UUID to fetch
 * @param {UseRoleOptions} options - Configuration options
 * @returns {UseRoleResult} Role data and query state
 *
 * @example
 * // Basic usage in edit form
 * const { role, isLoading } = useRole('uuid-123');
 *
 * @example
 * // Disabled until UUID is available
 * const { uuid } = useParams();
 * const { role } = useRole(uuid, { enabled: !!uuid });
 *
 * @example
 * // Testing with mock composition
 * jest.mock('@personnel/composition', () => ({
 *   roleUseCases: {
 *     getRoleByUuid: { execute: jest.fn().mockResolvedValue(mockRole) }
 *   }
 * }));
 */
export const useRole = (uuid: string, options: UseRoleOptions = {}): UseRoleResult => {
  const { enabled = true } = options;

  const query: UseQueryResult<Role, Error> = useQuery({
    queryKey: ROLES_QUERY_KEYS.detail(uuid),
    queryFn: () => getRoleUseCases().getRoleByUuid.execute(uuid), // ✅ Uses composed Use Case
    enabled: enabled && !!uuid, // Only run if enabled and UUID is provided
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 1, // Only retry once for 404 errors
  });

  return {
    role: query.data || null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error || null,
    refetch: query.refetch,
  };
};
